import { getHighlighterKey } from './utils'

export const RESTORE_DELAY_MS = 10
export const SAVE_BLOCK_DELAY_MS = 100

// a11y: hidden text read by screen readers so a highlight is conveyed on read, not by color alone.
// Stripped before every rangy serialize/deserialize and re-added after, so it never shifts rangy's
// character offsets. The nbsp prevents run-together speech (VoiceOver trims a plain space). See Ready#7707.
export const HIGHLIGHT_MARKER_CLASSNAME = 'highlight-sr-marker'
export const HIGHLIGHT_START_TEXT = 'highlight start\u00A0'
export const HIGHLIGHT_END_TEXT = '\u00A0highlight end'

// a11y: accessible-name prefix for a highlight made focusable in erase mode, so a keyboard user
// hears e.g. "Remove highlight: {text}, button" and Enter/Space removes the whole highlight.
export const ERASE_MARK_LABEL_PREFIX = 'Remove highlight: '

// a11y: builds a visually-hidden marker span read by screen readers (clip pattern via `sr-only`).
const createHighlightMarker = (text: string): HTMLSpanElement => {
  const marker = document.createElement('span')
  marker.className = `sr-only ${HIGHLIGHT_MARKER_CLASSNAME}`
  marker.textContent = text
  return marker
}

// a11y: removes the hidden "highlight start/end" markers. Must run before any rangy
// serialize/deserialize so the extra text does not shift rangy's character offsets.
export const removeHighlightMarkers = (container: HTMLElement): void => {
  container.querySelectorAll(`span.${HIGHLIGHT_MARKER_CLASSNAME}`).forEach(marker => marker.remove())
}

// a11y: brackets each highlight with hidden "highlight start" / "highlight end" text so a screen
// reader conveys the highlight on read (including highlights restored from a prior session).
// Idempotent: strips any existing markers first so re-running never doubles them.
export const addHighlightMarkers = (container: HTMLElement, className: string): void => {
  removeHighlightMarkers(container)

  container.querySelectorAll(`span.${className}`).forEach(mark => {
    // a11y: skip whitespace-only marks (e.g. a highlight split across an <em>) so an empty
    // highlight isn't announced as "highlight start … highlight end" with no text between.
    if (!mark.textContent?.trim()) return

    mark.insertBefore(createHighlightMarker(HIGHLIGHT_START_TEXT), mark.firstChild)
    mark.appendChild(createHighlightMarker(HIGHLIGHT_END_TEXT))
  })
}

// a11y: the highlight's visible text, excluding the hidden "highlight start/end" markers, used as
// the accessible name when the highlight is made a focusable erase button.
export const getMarkLabelText = (mark: Element): string => {
  let text = ''
  mark.childNodes.forEach(node => {
    const isMarker =
      node.nodeType === Node.ELEMENT_NODE &&
      (node as Element).classList.contains(HIGHLIGHT_MARKER_CLASSNAME)
    if (!isMarker) text += node.textContent || ''
  })
  return text.trim()
}

// a11y: in erase mode, turn each highlight into a focusable button so a keyboard user can Tab to a
// highlight and remove the whole thing with Enter/Space — mirroring the mouse click-to-erase.
export const setMarksErasable = (container: HTMLElement, className: string): void => {
  container.querySelectorAll(`span.${className}`).forEach(mark => {
    // a11y: skip a whitespace-only mark so it isn't a focusable, empty "Remove highlight:" button
    if (!getMarkLabelText(mark)) return

    mark.setAttribute('tabindex', '0')
    mark.setAttribute('role', 'button')
    mark.setAttribute('aria-label', `${ERASE_MARK_LABEL_PREFIX}${getMarkLabelText(mark)}`)
  })
}

// a11y: strip the erase-button attrs from one mark. Also required before rangy unwraps a mark on
// removal — rangy leaves any element that carries attributes it did not add.
export const clearMarkErasable = (mark: Element): void => {
  mark.removeAttribute('tabindex')
  mark.removeAttribute('role')
  mark.removeAttribute('aria-label')
}

// a11y: drop the erase affordances from all marks when erase mode turns off.
export const clearMarksErasable = (container: HTMLElement, className: string): void => {
  container.querySelectorAll(`span.${className}`).forEach(clearMarkErasable)
}

// a11y: rangy won't unwrap a fragment that still carries the erase-button attrs, so removing a
// highlight split across an inline element leaves class-less stubs that keep the "Remove highlight"
// role/label — a dead tab stop. Unwrap any such stub so nothing focusable lingers.
export const removeErasableStubs = (container: HTMLElement, className: string): void => {
  container.querySelectorAll(`span[aria-label^="${ERASE_MARK_LABEL_PREFIX}"]:not(.${className})`).forEach(stub => {
    const parent = stub.parentNode
    if (!parent) return

    while (stub.firstChild) {
      parent.insertBefore(stub.firstChild, stub)
    }
    parent.removeChild(stub)
  })
}

export const removeMarksFromContainer = (container: HTMLElement, className: string): void => {
  // a11y: drop the sr markers first so unwrapping the marks does not orphan them in the container
  removeHighlightMarkers(container)

  // a11y: also catch classless strays (all our marks keep data-container) so past failed erases clear
  const marksInContainer = container.querySelectorAll(`span.${className}, span[data-container]`)
  marksInContainer.forEach(mark => {
    const parent = mark.parentNode
    if (!parent) return

    while (mark.firstChild) {
      parent.insertBefore(mark.firstChild, mark)
    }
    parent.removeChild(mark)
  })
}

// Finds highlight mark element from selection
export const findMarkFromSelection = (selection: RangySelection, className: string): Element | null => {
  const anchorNode = selection.anchorNode
  if (!anchorNode) return null

  const element =
    anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentElement : (anchorNode as Element)

  return element?.closest(`span.${className}`) || null
}

export const saveHighlights = (highlighter: Highlighter, key: string): void => {
  const serialized = highlighter.serialize()
  localStorage.setItem(key, serialized)
}

export const withSaveBlock = (isSavingRef: React.MutableRefObject<boolean>, callback: () => void): void => {
  isSavingRef.current = true
  callback()
  setTimeout(() => {
    isSavingRef.current = false
  }, SAVE_BLOCK_DELAY_MS)
}

// Clear all persisted highlights for a specific assessment (test) and user.
export const clearHighlightsForAssessment = (userId: string, assessmentId: string): void => {
  if (!userId || !assessmentId) return

  const prefixes = ['q', 'p'].map(scope => getHighlighterKey(scope, userId, assessmentId, ''))

  // Iterate backwards so that removing items doesn't affect the indices of remaining keys.
  Object.keys(localStorage)
    .filter(key => prefixes.some(prefix => key.startsWith(prefix)))
    .forEach(key => {
      localStorage.removeItem(key)
    })
}
